import * as React from 'react';
import {
  BranchesOutlined,
  PullRequestOutlined,
  DiffOutlined,
  SyncOutlined,
  RobotOutlined
} from '@ant-design/icons';
import { message, Popover, Badge, Space } from 'antd';
import { localizedString } from '@locale/index';
import { AGENT_DEPLOY_MODE, AGENT_LAN, AGENT_MODE, API } from '@config/index';
import { ReviewDrawer, AgentDataNode, NiButton, niConfirm, TryItNow, FunctionTestingDrawer } from '@component/index';
import { agentToLocal, AgentInfo, deploymentInfoToLocal, DeploymentStatus } from '@interface/index';
import { isEqual, getOrgId, getAgentId, getAgentPathPrefix, isProviderAgent, isStructAgent, isPostgresProviderAgent } from '@lib/index';
import { store } from '@reducer/index';
import { debounce } from 'lodash';
import { globalModel } from '@reducer/global';
import { EditPermission } from '@permission/index';

export type VersionStatus = {
  diff: boolean;
  rebase: boolean;
  testedDiff: boolean;
  rebuildingRequired: boolean;
};

let versionStatus: VersionStatus = {
  diff: false,
  rebase: false,
  testedDiff: false,
  rebuildingRequired: false
};

export interface VersionObserver {
  onVersionChanged: (status?: VersionStatus) => void;
}

const Observers: VersionObserver[] = [];

export const AddVersionObserver = (observer: VersionObserver) => {
  if (Observers.indexOf(observer) === -1) {
    Observers.push(observer);
  }
};

export const RemoveVersionObserver = (observer: VersionObserver) => {
  const index = Observers.indexOf(observer);

  if (index !== -1) {
    Observers.splice(index, 1);
  }
};

export type VersionButtonProps = {
  history: any;
  agent: AgentInfo;
};

export type VersionButtonState = {
  status: VersionStatus;
  loading: boolean;
  drawerData?: any;
  drawerVisible: boolean;
  conflicting?: boolean;
  conflictAgentId?: string;
  demoVisible: boolean;
  funcTestingVisible: boolean;
  demoDisabled: boolean;
};

export class VersionButton extends React.Component<VersionButtonProps, VersionButtonState> {

  constructor(props: VersionButtonProps) {
    super(props);

    this.state = {
      status: versionStatus,
      loading: false,
      drawerVisible: false,
      demoVisible: false,
      funcTestingVisible: false,
      demoDisabled: false
    };
  }

  componentDidMount() {
    VersionButtons.push(this);
  }

  componentWillUnmount() {
    const index = VersionButtons.indexOf(this);
    if (index !== -1) {
      VersionButtons.splice(index, 1);
    }
    this.setState = () => { // do nothing
    };
  }

  onInit = async (trigger?: VersionObserver) => {

    if (!getAgentId()) {
      return;
    }

    this.setState({ loading: true, status: GetVersionStatus() });

    const res: any = await API.version.status()
      .catch((e: any) => {
        throw e;
      });

    const nextStatus = {
      diff: res.diff,
      rebase: res.rebase,
      testedDiff: res.tested_diff,
      rebuildingRequired: res.rebuilding_required
    };

    if (!isEqual(nextStatus, GetVersionStatus())) {
      saveVersionStatus(nextStatus);
      versionStatus = nextStatus;
      this.notifyObservers(trigger);
    }

    const { agent } = this.props;

    if (isProviderAgent() && agent && agent.deployMode === AGENT_DEPLOY_MODE.FRAMELY) {
      API.version.deployInfo()
        .then((dpInfo: any) => {

          const info = dpInfo && deploymentInfoToLocal(dpInfo);
          this.setState({
            demoDisabled: (isProviderAgent() &&
              info && info.status !== DeploymentStatus.Success)

          });
        });
    }

    this.setState({
      status: nextStatus,
      loading: false
    });
  }

  notifyObservers = (trigger?: VersionObserver) => {

    for (const observer of Observers) {
      if (observer === trigger) {
        return;
      }
      observer.onVersionChanged(GetVersionStatus());
    }
  }

  render() {
    const { agent } = this.props;
    const { status, loading, drawerVisible, drawerData, conflicting, conflictAgentId, demoVisible, funcTestingVisible, demoDisabled } = this.state;

    return <Space direction="horizontal" size={16} align="center">
      {
        drawerVisible && drawerData &&
        <ReviewDrawer
          disableApproval={false}
          loading={loading}
          conflicting={conflicting}
          visible={drawerVisible}
          data={drawerData}
          basePath={[]}
          rootNode={AgentDataNode}
          footerElements={conflicting
            ? []
            : this.getMergeButtons()
          }
          onClose={() => {
            this.setState({
              drawerVisible: false
            });
          }}
          resolveAgentId={conflictAgentId}
          onResolve={(resolvedList: any) => {
            this.setState({ loading: true });
            API.version.resolveConflicts(resolvedList, conflictAgentId)
              .then((res: any) => {
                if (agent.structKey && conflictAgentId === agent.structKey.agentId) {
                  message.success('struct layer rebase is successful, you may still need rebase language layer');
                }
                this.notifyObservers();
                this.setState({
                  drawerVisible: false,
                  drawerData: undefined
                });
              })
              .catch((err: any) => {
                message.error(err.message);
                throw new Error(err.message);
              })
              .finally(() => {
                this.setState({
                  loading: false
                }, this.onRefresh);
              });
          }}
        />
      }
      {
        < TryItNow
          visible={demoVisible}
          onClose={() => {
            this.setState({ demoVisible: false });
          }}
        />
      }
      {funcTestingVisible &&
        <FunctionTestingDrawer
          visible={funcTestingVisible}
          onClose={() => {
            this.setState({ funcTestingVisible: false });
          }}
        />
      }
      {
        <div id="uinique-btn-commit" className="button-area" onClick={async () => {
          if (status.rebase) {
            return;
          }

          this.setState({ loading: true });

          await API.version.test()
            .catch((e: any) => {
              this.setState({ loading: false });

              if (e.message === 'confirm info' || e.message === '确认信息') {
                niConfirm({
                  width: '60%',
                  title: localizedString('common_error_info'),
                  content: <pre style={{
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-all'
                  }}>{e.data}</pre>

                });
              } else {
                message.warning(e.message
                  ? <pre style={{ whiteSpace: 'pre-wrap', textAlign: 'start' }}>{
                    e.message
                  }
                  </pre>
                  : localizedString('common_test_failure'));
              }

              throw e;
            }).finally(() => {
              this.onRefresh();
            });

          this.setState({ loading: false });
          store.dispatch({
            type: 'global/update_state',
            payload: {
              demoConnected: false
            }
          });
        }}>
          <Badge
            dot={status.rebuildingRequired}>
            <BranchesOutlined
              key="publish-btn"
              className={`header-btn white ${status.rebase ? 'disabled' : ''}`}
              spin={loading}
            />
          </Badge>
          <span
            style={{
              marginLeft: 4,
              color: status.rebase
                ? 'lightgray'
                : ''
            }}
          >{localizedString((isStructAgent() && !isProviderAgent()) ? 'propagate' : 'sidebar_commit')}</span>
        </div>
      }
      {
        <div
          className={`button-area ${!status.rebase ? 'disabled' : ''}`}
          onClick={async () => {
            if (!status.rebase) {
              return;
            }

            this.setState({ loading: true });
            await API.version.rebase()
              .then((res: any) => {
                this.setState({ loading: false });
                this.notifyObservers();
                this.onRefresh();
              })
              .catch((e: any) => {
                if (e.data && e.data.messages && e.data.messages.length) {
                  this.setState({ loading: false });
                  message.warning(JSON.stringify(e.data.messages, undefined, 2));
                  throw e;
                }

                if (e.data
                  && e.data.conflict) {
                  API.version.conflictData(e.data.agent_id).then((res: any) => {
                    this.setState({
                      loading: false,
                      drawerData: agentToLocal(res),
                      drawerVisible: true,
                      conflicting: true,
                      conflictAgentId: e.data.agent_id
                    });
                  }).catch((e: any) => {
                    this.setState({ loading: false });
                    message.warning(e.message ? e.message : localizedString('common_rebase_failure'));
                    throw e;
                  });
                } else {
                  this.setState({ loading: false });
                  message.warning(e.message ? e.message : localizedString('common_rebase_failure'));
                  throw e;
                }
              });
          }}>
          <SyncOutlined
            key="rebase-btn"
            className={`header-btn red ${!status.rebase ? 'disabled' : ''}`}
            spin={loading}
            style={{
              marginRight: 4,
              color: status.rebase
                ? 'red' :
                'lightgrary'
            }}
          />
          <span
            key={`${status.rebase}`}
            style={{
              color: status.rebase
                ? 'red' :
                'lightgrary'
            }}
          >{localizedString('sidebar_rebase')}</span>
        </div>
      }
      {
        <div className="button-area" onClick={async () => {

          this.setState({ loading: true });
          const res = await API.version.review()
            .catch((e: Error) => {
              this.setState({ loading: false });
              message.error(e.message);
              throw e;
            });
          this.setState({ loading: false });
          const reviewData = agentToLocal(res);

          this.setState({
            drawerData: reviewData,
            drawerVisible: true,
            conflicting: false
          });
        }}>
          <Badge
            dot={status.diff}>
            <DiffOutlined
              key="diff-btn"
              className={`header-btn white`}
              spin={loading}
            // style={{ marginRight: 4 }}
            />
          </Badge>
          <span style={{ marginLeft: 4 }}>{localizedString('sidebar_diff')}</span>
        </div>
      }
      {/* {
        agent &&
        agent.id &&
        // !isReadMode() &&
        ((agent.mode !== AGENT_MODE.LIBRARY &&
          agent.language !== AGENT_LAN.STRUCT) ||
          isProviderAgent()) && !isPostgresProviderAgent() &&
        EditPermission() &&
        <>
          <div id="unique-btn-debug" className={`button-area ${demoDisabled ? 'disabled' : ''}`} onClick={() => {
            if (demoDisabled) {
              return;
            }

            if (isProviderAgent()) {
              this.setState({ funcTestingVisible: true });
            } else {
              this.setState({ demoVisible: true });
            }
          }}>
            <RobotOutlined
              disabled={demoDisabled}
              className={`${demoDisabled ? 'disabled' : ''}`}
              style={{ marginRight: 4 }}
            />
            {localizedString('sidebar_try_it_now')}
          </div>
        </>
      } */}
    </Space>;
  }

  handleNewPr = debounce(async () => {
    await API.review.newPr().catch((err: any) => {
      message.error(err.message);
      throw err;
    });
    this.onInit();
  }, 2000, { leading: true, trailing: false });

  getMergeButtons = () => {
    return [
      <NiButton
        type="default"
        key="revert-to-master"
        onClick={async () => {
          this.revert('master');
        }}
      >
        {localizedString('diff_info_revert_to_master')}
      </NiButton>,
      <NiButton
        type="primary"
        key="revert-to-commit"
        onClick={async () => {
          this.revert('commit');
        }}
      >
        {localizedString('diff_info_revert_to_commit')}
      </NiButton>
    ];
  };

  revert = async (to: 'master' | 'commit') => {
    this.setState({ loading: true });
    await API.version.revert(to)
      .catch((err: Error) => {
        this.setState({ loading: false });
        message.error(err.message);
        throw err;
      });

    this.setState({
      loading: false,
      drawerVisible: false,
      drawerData: undefined
    });

    await store.dispatch(
      globalModel.effects.fetchParameters({ id: getAgentId(), forceUpdate: true })
    );
    this.notifyObservers();
    this.onRefresh();
  }

  onRefresh = () => {
    this.onInit();
  }
}

const VersionButtons: VersionButton[] = [];

const K_VERSION_STATUS = 'KEY_VERSION_STATUS';

const saveVersionStatus = (status: VersionStatus) => {
  window.sessionStorage.setItem(K_VERSION_STATUS, JSON.stringify(status));
};

export const GetVersionStatus = () => {
  const str = window.sessionStorage.getItem(K_VERSION_STATUS);
  return str && JSON.parse(str) || versionStatus;
};

export const RefreshVersion = (trigger?: VersionObserver) => {

  for (const button of VersionButtons) {

    button.onInit(trigger);
  }
};
